import React, { useState, useEffect, useCallback } from 'react';
import { ShieldCheck, Check, Lock } from 'lucide-react';
import { paymentService } from '../../services';
import { Card, Badge, Button, Dialog, Toast } from '../ds';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { formatPlanPrice, formatPriceSuffix } from '../../utils/plans';

const FONT = "'IBM Plex Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
const BRAND = '#1E4FBE';

// Plan-Übersicht (AUD-61): Name, Preis, Intervall und Leistungsmerkmale kommen
// ausschließlich aus GET /payments/plans (Quelle: Stripe-Price + zentraler
// Plan-Katalog im Backend). Welcher Plan gebucht ist, liefert getSubscription
// (`plan`) — zusammen mit dem lokalen Abo-Status aus der DB.

function formatDate(value, opts = { day: '2-digit', month: 'long', year: 'numeric' }) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString('de-DE', opts);
  } catch {
    return null;
  }
}

const formatDateShort = (value) => formatDate(value, { day: '2-digit', month: '2-digit', year: 'numeric' });

// Statusanzeige des gebuchten Plans. Eine zum Periodenende ausgesprochene Kündigung
// hat Vorrang: Stripe hält die Subscription bis dahin auf 'active'/'trialing', für
// den Kunden ist der relevante Zustand aber „gekündigt zum …".
function statusBadge(status, cancelAtPeriodEnd, periodEndShort) {
  if (cancelAtPeriodEnd) {
    return { tone: 'warning', text: periodEndShort ? `Gekündigt zum ${periodEndShort}` : 'Gekündigt' };
  }
  switch (status) {
    // Trials werden seit AUD-58 nicht mehr erzeugt; Label bleibt für Alt-Abos.
    case 'trialing': return { tone: 'info', text: 'Testphase' };
    case 'active': return { tone: 'secure', text: 'Abo aktiv' };
    case 'past_due': return { tone: 'warning', text: 'Zahlung fehlgeschlagen' };
    case 'cancelled': return { tone: 'critical', text: 'Gekündigt' };
    default: return null;
  }
}

const FeatureList = ({ features, tone }) => (
  <ul style={{ listStyle: 'none', padding: 0, margin: '18px 0 0', display: 'flex', flexDirection: 'column', gap: 10, flexGrow: 1 }}>
    {features.map((f) => (
      <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13.5, color: '#2A3140', lineHeight: 1.45, fontFamily: FONT }}>
        <Check size={18} color={tone} style={{ flexShrink: 0, marginTop: 1 }} />
        {f}
      </li>
    ))}
  </ul>
);

const AbonnementTab = () => {
  const isMobile = useIsMobile();
  const [sub, setSub] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [subRes, plansRes] = await Promise.all([
        paymentService.getSubscription(),
        paymentService.getPlans(),
      ]);
      setSub(subRes.data);
      setPlans(plansRes.data?.plans || []);
    } catch {
      setError('Abo-Status konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openPortal = async ({ cancel = false } = {}) => {
    setRedirecting(true);
    try {
      const { data } = await paymentService.createPortalSession({ returnPath: '/settings/abonnement', cancel });
      window.location.href = data.url;
    } catch (err) {
      setToast({ tone: 'critical', message: err?.response?.data?.error || 'Verwaltungsportal konnte nicht geöffnet werden.' });
      setRedirecting(false);
    }
  };

  const startCheckout = async () => {
    setRedirecting(true);
    try {
      const { data } = await paymentService.createCheckoutSession();
      window.location.href = data.url;
    } catch (err) {
      setToast({ tone: 'critical', message: err?.response?.data?.error || 'Checkout konnte nicht gestartet werden.' });
      setRedirecting(false);
    }
  };

  if (loading) return (
    <Card padding={22}>
      <div style={{ color: '#8A93A3', fontSize: 13, fontFamily: FONT }}>Lädt…</div>
    </Card>
  );

  if (error || !plans.length) return (
    <Card padding={22}>
      <p style={{ margin: '0 0 14px', fontSize: 13, color: '#C22B31', fontFamily: FONT }}>
        {error || 'Die Tarife konnten nicht geladen werden.'}
      </p>
      <Button size="md" onClick={load}>Erneut versuchen</Button>
    </Card>
  );

  const status = sub?.status || 'none';
  const cancelAtPeriodEnd = !!sub?.cancelAtPeriodEnd;
  const periodEnd = formatDate(sub?.currentPeriodEnd);
  const periodEndShort = formatDateShort(sub?.currentPeriodEnd);
  const currentPlanKey = sub?.plan || 'free';
  const badge = statusBadge(status, cancelAtPeriodEnd, periodEndShort);
  // Kündigen nur anbieten, wenn es etwas zu kündigen gibt und nicht bereits
  // gekündigt wurde — sonst tritt „Abonnement verwalten" (Reaktivierung) an
  // die Stelle des Kündigen-Buttons.
  const canCancel = (status === 'active' || status === 'trialing') && !cancelAtPeriodEnd;

  // Auf Mobile stapeln die Karten — der gebuchte Plan steht dann oben.
  const orderedPlans = isMobile
    ? [...plans].sort((a, b) => Number(b.key === currentPlanKey) - Number(a.key === currentPlanKey))
    : plans;

  const renderNotice = (plan) => {
    if (plan.key !== currentPlanKey) {
      // Free-Karte, während Standard gebucht ist: Kündigung wird bewusst nur an
      // einer Stelle angeboten (auf der Karte des aktiven Plans).
      if (!plan.purchasable) {
        return cancelAtPeriodEnd && periodEnd
          ? `Ihr Konto wechselt am ${periodEnd} auf ${plan.name}.`
          : `Nach einer Kündigung fällt Ihr Konto automatisch auf ${plan.name} zurück.`;
      }
      return null;
    }
    // Free ist der aktive Plan → es gibt kein Abo, über das zu berichten wäre
    // (ein `currentPeriodEnd` aus einem beendeten Abo darf hier nicht als
    // „nächste Abrechnung" auftauchen).
    if (!plan.purchasable) return null;
    if (cancelAtPeriodEnd && periodEnd) {
      return `Ihr Abo ist gekündigt und läuft noch bis zum ${periodEnd}. Danach fällt Ihr Konto auf den Free-Tier zurück. Sie können die Kündigung bis dahin über „Abonnement verwalten" widerrufen.`;
    }
    if (status === 'past_due') {
      return 'Ihre letzte Zahlung ist fehlgeschlagen. Bitte aktualisieren Sie Ihre Zahlungsdaten über „Abonnement verwalten".';
    }
    if (status === 'trialing' && periodEnd) {
      const price = formatPlanPrice(plan.price);
      return `Ihre Testphase endet am ${periodEnd}. Danach läuft ${plan.name} für ${price} ${formatPriceSuffix(plan.price)} weiter.`;
    }
    if (periodEnd) return `Nächste Abrechnung am ${periodEnd}.`;
    return null;
  };

  const noticeTone = (plan) =>
    (plan.key === currentPlanKey && (cancelAtPeriodEnd || status === 'past_due')) ? 'var(--warn-700)' : '#4A5262';

  const renderActions = (plan) => {
    const isCurrent = plan.key === currentPlanKey;

    if (isCurrent && plan.purchasable) {
      return (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Button variant="secondary" onClick={() => openPortal()} disabled={redirecting}>
            {redirecting ? 'Wird geöffnet …' : 'Abonnement verwalten'}
          </Button>
          {canCancel && (
            <Button variant="danger" onClick={() => setConfirmCancel(true)} disabled={redirecting}>
              Abonnement kündigen
            </Button>
          )}
        </div>
      );
    }

    // Wechsel auf den kostenpflichtigen Plan: ohne bestehendes Abo gibt es beim
    // Zahlungsanbieter noch kein Kundenportal — der Wechsel läuft daher über den
    // bestehenden Checkout (kein zusätzlicher In-App-Flow).
    if (!isCurrent && plan.purchasable) {
      return (
        <Button fullWidth onClick={startCheckout} disabled={redirecting} leadingIcon={<Lock width={15} height={15} />}>
          {redirecting ? 'Wird gestartet …' : `${plan.name} buchen`}
        </Button>
      );
    }

    return null;
  };

  return (
    <>
      {/* 24px statt 16: das Ribbon des aktiven Plans ragt 11px über die Kartenoberkante. */}
      <p style={{ margin: '0 0 24px', fontSize: 13, color: '#8A93A3', fontFamily: FONT }}>
        Ihr aktueller Tarif ist hervorgehoben. Alle Preise verstehen sich netto zzgl. USt.
      </p>

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : `repeat(${orderedPlans.length}, minmax(0, 1fr))`,
        gap: 16,
        alignItems: 'stretch',
      }}>
        {orderedPlans.map((plan) => {
          const isCurrent = plan.key === currentPlanKey;
          const notice = renderNotice(plan);
          const actions = renderActions(plan);
          const accent = plan.purchasable ? 'var(--brand-600)' : 'var(--secure-600)';

          return (
            <Card
              key={plan.key}
              padding={26}
              style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                // Card kappt Überstehendes (overflow: hidden) — das Ribbon des aktiven
                // Plans ragt bewusst über die Oberkante und braucht daher sichtbaren Overflow.
                ...(isCurrent ? { border: `1.5px solid ${BRAND}`, overflow: 'visible' } : {}),
              }}
            >
              {isCurrent && (
                <div style={{
                  position: 'absolute', top: -11, right: 20,
                  background: BRAND, color: '#fff', fontSize: 11, fontWeight: 700,
                  letterSpacing: '0.04em', padding: '3px 10px', borderRadius: 999,
                  textTransform: 'uppercase', fontFamily: FONT, lineHeight: '16px',
                }}>Aktueller Plan</div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                {plan.purchasable && <ShieldCheck size={19} color="var(--brand-600)" />}
                <span style={{ fontSize: 17, fontWeight: 700, color: '#101623', fontFamily: FONT }}>{plan.name}</span>
                {/* Abo-Status nur am kostenpflichtigen Plan — Free hat keinen. */}
                {isCurrent && plan.purchasable && badge && <Badge tone={badge.tone} dot>{badge.text}</Badge>}
              </div>

              <div style={{ fontSize: 14, color: '#4A5262', fontFamily: FONT }}>
                <span style={{ fontSize: 26, fontWeight: 700, color: '#101623' }}>{formatPlanPrice(plan.price)}</span>
                <span style={{ marginLeft: 6 }}>{formatPriceSuffix(plan.price)}</span>
              </div>
              {plan.tagline && (
                <p style={{ margin: '8px 0 0', fontSize: 13, color: '#8A93A3', fontFamily: FONT }}>{plan.tagline}</p>
              )}

              <FeatureList features={plan.features} tone={accent} />

              {(notice || actions) && (
                <div style={{ borderTop: '1px solid var(--border-subtle)', margin: '20px 0 0', paddingTop: 18 }}>
                  {notice && (
                    <p style={{ fontSize: 13, color: noticeTone(plan), margin: actions ? '0 0 16px' : 0, lineHeight: 1.55, fontFamily: FONT }}>
                      {notice}
                    </p>
                  )}
                  {actions}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      <p style={{ fontSize: 12, color: '#8A93A3', margin: '16px 0 0', fontFamily: FONT }}>
        Zahlungsmethode aktualisieren und Rechnungen einsehen erfolgt im Verwaltungsportal.
      </p>

      <Dialog
        open={confirmCancel}
        onClose={() => setConfirmCancel(false)}
        title="Abonnement kündigen?"
        description="Die Kündigung wird zum Ende der laufenden Abrechnungsperiode wirksam. Bis dahin behalten Sie vollen Zugriff."
        footer={(
          <>
            <Button variant="secondary" onClick={() => setConfirmCancel(false)} disabled={redirecting}>Zurück</Button>
            <Button variant="danger" onClick={() => openPortal({ cancel: true })} disabled={redirecting}>
              {redirecting ? 'Wird geöffnet …' : 'Weiter zur Kündigung'}
            </Button>
          </>
        )}
      >
        {periodEnd ? `Ihr Zugang bleibt bis zum ${periodEnd} bestehen.` : 'Ihr Zugang bleibt bis zum Ende der laufenden Periode bestehen.'}
      </Dialog>

      {toast && <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />}
    </>
  );
};

export default AbonnementTab;
